import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { isStaffRole } from '../lib/adminRoles';
import { getClinicInfo, updateClinicInfo } from '../lib/api';
import {
  CLINIC_FIELD_META,
  DEFAULT_HOME_FEATURES,
  HOME_EDITOR_SECTIONS,
  mergeClinicInfoForEditor,
} from '../lib/clinicSiteDefaults';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { ChevronDown, Plus, Trash2, Save, Map, ExternalLink, MapPin } from 'lucide-react';
import { buildGoogleMapsSearchUrl, getAddressDisplayLabel } from '../lib/googleMaps';
import SiteImageField from '../components/SiteImageField';
import {
  BrandingPreview,
  FeaturesPreview,
  HeroPreview,
  HoursPreview,
  ProductsSectionPreview,
  ReviewsSectionPreview,
  CtaSectionPreview,
  ContactHomePreview,
  ServicesSectionPreview,
} from '../components/sitePublicPreviews';

function SectionCard({ id, title, hint, children, preview }) {
  return (
    <Card id={id} className="scroll-mt-24">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {hint ? <p className="text-sm text-muted-foreground font-normal">{hint}</p> : null}
      </CardHeader>
      <CardContent>
        <div className="grid gap-8 xl:grid-cols-2 xl:items-start">
          <div className="space-y-4">{children}</div>
          <div className="xl:sticky xl:top-4">{preview}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function LabelWithHint({ htmlFor, fieldKey, label, hint }) {
  const meta = fieldKey ? CLINIC_FIELD_META[fieldKey] : null;
  const displayLabel = label || meta?.label || htmlFor;
  const displayHint = hint || meta?.hint;
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{displayLabel}</Label>
      {displayHint ? (
        <p className="text-xs text-muted-foreground leading-snug">{displayHint}</p>
      ) : null}
    </div>
  );
}

function AddressField({ value, onChange }) {
  const mapsUrl = buildGoogleMapsSearchUrl(value);
  const hasAddress = Boolean(getAddressDisplayLabel(value));

  return (
    <div className="space-y-2">
      <LabelWithHint htmlFor="address" fieldKey="address" />
      <Input
        id="address"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ej. C. Guillermo Prieto 20, Cd Sahagún — o pega un enlace de Google Maps"
      />
      {hasAddress ? (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <MapPin className="w-4 h-4" />
          Abrir en Google Maps
          <ExternalLink className="w-3.5 h-3.5 opacity-70" />
        </a>
      ) : null}
    </div>
  );
}

function FieldInput({ fieldKey, value, onChange, type = 'text', rows, required, placeholder }) {
  const id = fieldKey;
  const common = {
    id,
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    required,
    placeholder,
  };
  return (
    <div className="space-y-2">
      <LabelWithHint htmlFor={id} fieldKey={fieldKey} />
      {rows ? (
        <Textarea {...common} rows={rows} />
      ) : (
        <Input {...common} type={type} />
      )}
    </div>
  );
}

const FEATURE_ICON_OPTIONS = [
  'shield',
  'heart',
  'clock',
  'calendar',
  'sparkles',
  'star',
  'thumbs-up',
  'users',
  'award',
  'check-circle',
];

export default function AdminSitePublic() {
  const { refresh: refreshBranding } = useClinicBranding();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [servicesText, setServicesText] = useState('');
  const [featureRows, setFeatureRows] = useState(DEFAULT_HOME_FEATURES);

  useEffect(() => {
    if (!authLoading && user && !isStaffRole(user.role)) {
      toast.error('Se requieren permisos de administrador.');
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || !isStaffRole(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getClinicInfo();
        if (cancelled) return;
        const d = mergeClinicInfoForEditor(raw);
        setDraft(d);
        setServicesText((d.services_bullets || []).join('\n'));
        setFeatureRows(
          Array.isArray(d.home_features) && d.home_features.length > 0
            ? d.home_features.map((x) => ({ icon: x.icon, title: x.title, desc: x.desc }))
            : DEFAULT_HOME_FEATURES.map((f) => ({ ...f })),
        );
      } catch {
        toast.error('No se pudo cargar la información de la clínica.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setField = (key, value) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  /** Guarda el texto tal cual mientras se escribe (permite espacios). El trim va al guardar. */
  const setOptionalText = (key, value) => {
    setField(key, value);
  };

  const OPTIONAL_TEXT_KEYS = [
    'site_tagline_short',
    'footer_blurb',
    'copyright_name',
    'hero_badge_text',
    'hero_title_prefix',
    'hero_title_highlight',
    'hero_subtitle',
    'services_section_title',
    'services_section_intro',
    'featured_products_title',
    'featured_products_intro',
    'reviews_section_title',
    'cta_title',
    'cta_subtitle',
    'contact_section_title',
    'footer_hours_weekdays',
    'footer_hours_lunch',
    'footer_hours_weekend',
  ];

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    try {
      const services_bullets = servicesText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const normalized = { ...draft };
      for (const key of OPTIONAL_TEXT_KEYS) {
        if (!(key in normalized)) continue;
        const trimmed = String(normalized[key] ?? '').trim();
        normalized[key] = trimmed || null;
      }
      const payload = {
        ...normalized,
        services_bullets,
        home_features: featureRows.map((r) => ({
          icon: r.icon || 'shield',
          title: r.title || '',
          desc: r.desc || '',
        })),
      };
      const saved = await updateClinicInfo(payload);
      const hydrated = mergeClinicInfoForEditor(saved);
      setDraft(hydrated);
      setServicesText((hydrated.services_bullets || []).join('\n'));
      await refreshBranding();
      toast.success('Cambios del sitio guardados.');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading || !draft) {
    return (
      <div className="text-muted-foreground animate-pulse" data-testid="admin-site-loading">
        Cargando editor del sitio…
      </div>
    );
  }

  const brandName = draft.name;

  return (
    <div className="max-w-7xl space-y-8" data-testid="admin-configura-sitio">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Configura Sitio</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Edita textos e imágenes de la página principal. Pulse Guardar para publicar los cambios.
          </p>
        </div>
        <Button type="button" variant="outline" asChild className="shrink-0">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" />
            Abrir sitio en nueva pestaña
          </a>
        </Button>
      </div>

      <Collapsible open={mapOpen} onOpenChange={setMapOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 px-6 py-4 text-left hover:bg-muted/40 transition-colors rounded-t-xl"
            >
              <span className="flex items-center gap-2 font-medium text-foreground">
                <Map className="w-4 h-4 text-primary" />
                Mapa del home — saltar a una sección
              </span>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${mapOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-4">
              <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                {HOME_EDITOR_SECTIONS.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="block rounded-lg border border-border px-3 py-2 hover:bg-sage-50 hover:border-primary/30 transition-colors"
                    >
                      <span className="font-medium text-foreground">{section.title}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {section.description}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <form onSubmit={handleSave} className="space-y-8">
        <SectionCard
          id="edit-brand"
          title="Marca y contacto"
          hint="Cabecera verde, barra de navegación, pie del sitio y página /contacto."
          preview={
            <BrandingPreview
              name={draft.name}
              logoUrl={draft.logo_url}
              phone={draft.phone}
              email={draft.email}
              address={draft.address}
              siteTaglineShort={draft.site_tagline_short}
              footerBlurb={draft.footer_blurb}
              copyrightName={draft.copyright_name}
              footerHoursWeekdays={draft.footer_hours_weekdays}
              footerHoursLunch={draft.footer_hours_lunch}
              footerHoursWeekend={draft.footer_hours_weekend}
            />
          }
        >
          <SiteImageField
            label={CLINIC_FIELD_META.logo_url.label}
            url={draft.logo_url}
            onChange={(u) => setField('logo_url', u)}
            inputId="logo_url"
            previewVariant="logo"
          />
          <p className="text-xs text-muted-foreground -mt-2">{CLINIC_FIELD_META.logo_url.hint}</p>
          <FieldInput
            fieldKey="name"
            value={draft.name}
            onChange={(v) => setField('name', v)}
            required
          />
          <FieldInput
            fieldKey="site_tagline_short"
            value={draft.site_tagline_short || ''}
            onChange={(v) => setOptionalText('site_tagline_short', v)}
            placeholder="Opcional; si está vacío se usa la dirección resumida"
          />
          <FieldInput fieldKey="phone" value={draft.phone} onChange={(v) => setField('phone', v)} />
          <FieldInput
            fieldKey="email"
            type="email"
            value={draft.email}
            onChange={(v) => setField('email', v)}
          />
          <AddressField value={draft.address} onChange={(v) => setField('address', v)} />
          <FieldInput
            fieldKey="description"
            value={draft.description}
            onChange={(v) => setField('description', v)}
            rows={3}
          />
          <FieldInput
            fieldKey="footer_blurb"
            value={draft.footer_blurb || ''}
            onChange={(v) => setOptionalText('footer_blurb', v)}
            rows={3}
          />
          <FieldInput
            fieldKey="copyright_name"
            value={draft.copyright_name || ''}
            onChange={(v) => setOptionalText('copyright_name', v)}
          />
        </SectionCard>

        <SectionCard
          id="edit-hero"
          title="Hero (inicio)"
          hint="Primera pantalla al entrar al sitio: título grande, subtítulo, botones y foto."
          preview={
            <HeroPreview
              brandName={brandName}
              badge={draft.hero_badge_text}
              titlePrefix={draft.hero_title_prefix}
              titleHighlight={draft.hero_title_highlight}
              subtitle={draft.hero_subtitle}
              heroImageUrl={draft.hero_image_url}
            />
          }
        >
          <FieldInput
            fieldKey="hero_badge_text"
            value={draft.hero_badge_text || ''}
            onChange={(v) => setOptionalText('hero_badge_text', v)}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput
              fieldKey="hero_title_prefix"
              value={draft.hero_title_prefix || ''}
              onChange={(v) => setOptionalText('hero_title_prefix', v)}
            />
            <FieldInput
              fieldKey="hero_title_highlight"
              value={draft.hero_title_highlight || ''}
              onChange={(v) => setOptionalText('hero_title_highlight', v)}
            />
          </div>
          <FieldInput
            fieldKey="hero_subtitle"
            value={draft.hero_subtitle || ''}
            onChange={(v) => setOptionalText('hero_subtitle', v)}
            rows={3}
          />
          <SiteImageField
            label={CLINIC_FIELD_META.hero_image_url.label}
            url={draft.hero_image_url}
            onChange={(u) => setField('hero_image_url', u)}
            inputId="hero_image_url"
            previewVariant="hero"
          />
          <p className="text-xs text-muted-foreground -mt-2">{CLINIC_FIELD_META.hero_image_url.hint}</p>
        </SectionCard>

        <SectionCard
          id="edit-features"
          title="Tarjetas de valores"
          hint="Cuatro columnas con icono, título y texto breve, justo debajo del hero."
          preview={<FeaturesPreview featureRows={featureRows} />}
        >
          {featureRows.map((row, i) => (
            <div key={i} className="grid gap-3 p-4 border border-border rounded-xl sm:grid-cols-12 items-end">
              <div className="sm:col-span-3 space-y-2">
                <Label>Icono (Lucide)</Label>
                <p className="text-xs text-muted-foreground">Icono de la tarjeta {i + 1} en el home.</p>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={row.icon}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, icon: v } : r)));
                  }}
                >
                  {FEATURE_ICON_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-4 space-y-2">
                <Label>Título</Label>
                <Input
                  value={row.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, title: v } : r)));
                  }}
                />
              </div>
              <div className="sm:col-span-4 space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={row.desc}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, desc: v } : r)));
                  }}
                />
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => setFeatureRows((rows) => rows.filter((_, j) => j !== i))}
                  disabled={featureRows.length <= 1}
                  aria-label="Eliminar fila"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => setFeatureRows((rows) => [...rows, { icon: 'shield', title: '', desc: '' }])}
          >
            <Plus className="w-4 h-4" />
            Añadir tarjeta
          </Button>
        </SectionCard>

        <SectionCard
          id="edit-services"
          title="Servicios (lista con viñetas)"
          hint="Sección a mitad del home: título, párrafo, lista de servicios e imagen lateral."
          preview={
            <ServicesSectionPreview
              title={draft.services_section_title}
              intro={draft.services_section_intro}
              bulletsText={servicesText}
              imageUrl={draft.clinic_section_image_url}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="services_section_title"
            value={draft.services_section_title || ''}
            onChange={(v) => setOptionalText('services_section_title', v)}
          />
          <FieldInput
            fieldKey="services_section_intro"
            value={draft.services_section_intro || ''}
            onChange={(v) => setOptionalText('services_section_intro', v)}
            rows={3}
          />
          <div className="space-y-2">
            <LabelWithHint htmlFor="services_bullets" fieldKey="services_bullets" />
            <Textarea
              id="services_bullets"
              rows={8}
              value={servicesText}
              onChange={(e) => setServicesText(e.target.value)}
            />
          </div>
          <SiteImageField
            label={CLINIC_FIELD_META.clinic_section_image_url.label}
            url={draft.clinic_section_image_url}
            onChange={(u) => setField('clinic_section_image_url', u)}
            inputId="clinic_section_image_url"
            previewVariant="banner"
          />
          <p className="text-xs text-muted-foreground -mt-2">
            {CLINIC_FIELD_META.clinic_section_image_url.hint}
          </p>
        </SectionCard>

        <SectionCard
          id="edit-products"
          title="Productos destacados"
          hint="Título e intro sobre las tarjetas del catálogo. Los productos se gestionan en Admin → Productos."
          preview={
            <ProductsSectionPreview
              featuredProductsTitle={draft.featured_products_title}
              featuredProductsIntro={draft.featured_products_intro}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="featured_products_title"
            value={draft.featured_products_title || ''}
            onChange={(v) => setOptionalText('featured_products_title', v)}
          />
          <FieldInput
            fieldKey="featured_products_intro"
            value={draft.featured_products_intro || ''}
            onChange={(v) => setOptionalText('featured_products_intro', v)}
            rows={2}
          />
        </SectionCard>

        <SectionCard
          id="edit-reviews"
          title="Reseñas en el home"
          hint="Solo el título de la sección. Las opiniones se moderan en Admin → Reseñas."
          preview={
            <ReviewsSectionPreview
              reviewsSectionTitle={draft.reviews_section_title}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="reviews_section_title"
            value={draft.reviews_section_title || ''}
            onChange={(v) => setOptionalText('reviews_section_title', v)}
          />
        </SectionCard>

        <SectionCard
          id="edit-cta"
          title="Llamada a la acción (CTA)"
          hint="Franja verde con botón «Agendar Cita Ahora», antes del bloque de contacto."
          preview={
            <CtaSectionPreview
              ctaTitle={draft.cta_title}
              ctaSubtitle={draft.cta_subtitle}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="cta_title"
            value={draft.cta_title || ''}
            onChange={(v) => setOptionalText('cta_title', v)}
          />
          <FieldInput
            fieldKey="cta_subtitle"
            value={draft.cta_subtitle || ''}
            onChange={(v) => setOptionalText('cta_subtitle', v)}
          />
        </SectionCard>

        <SectionCard
          id="edit-contact"
          title="Contacto en el home"
          hint="Título del bloque «Encuéntranos». Teléfono, correo y dirección: sección Marca y contacto."
          preview={
            <ContactHomePreview
              contactSectionTitle={draft.contact_section_title}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="contact_section_title"
            value={draft.contact_section_title || ''}
            onChange={(v) => setOptionalText('contact_section_title', v)}
          />
        </SectionCard>

        <SectionCard
          id="edit-hours"
          title="Horarios (texto libre)"
          hint="Tres líneas de horario en la columna derecha del bloque contacto y en el pie del sitio."
          preview={
            <HoursPreview
              footerHoursWeekdays={draft.footer_hours_weekdays}
              footerHoursLunch={draft.footer_hours_lunch}
              footerHoursWeekend={draft.footer_hours_weekend}
              address={draft.address}
              phone={draft.phone}
              email={draft.email}
              contactSectionTitle={draft.contact_section_title}
              brandName={brandName}
            />
          }
        >
          <FieldInput
            fieldKey="footer_hours_weekdays"
            value={draft.footer_hours_weekdays || ''}
            onChange={(v) => setOptionalText('footer_hours_weekdays', v)}
          />
          <FieldInput
            fieldKey="footer_hours_lunch"
            value={draft.footer_hours_lunch || ''}
            onChange={(v) => setOptionalText('footer_hours_lunch', v)}
          />
          <FieldInput
            fieldKey="footer_hours_weekend"
            value={draft.footer_hours_weekend || ''}
            onChange={(v) => setOptionalText('footer_hours_weekend', v)}
          />
        </SectionCard>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="gap-2 rounded-full">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}
